// admin/src/pages/ManageBookings.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRecycleBin } from '../context/RecycleBinContext';
import './ManageBookings.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://tripsie.in/api';

const STATUS_CONFIG = {
  all:       { label:'All Status', dot:'#1a1e18', bg:'#f4f4f4', color:'#1a1e18' },
  requested: { label:'Requested',  dot:'#7c3aed', bg:'#ede9fe', color:'#4c1d95' },
  pending:   { label:'Pending',    dot:'#d97706', bg:'#fef3c7', color:'#92400e' },
  confirmed: { label:'Confirmed',  dot:'#16a34a', bg:'#dcfce7', color:'#14532d' },
  cancelled: { label:'Cancelled',  dot:'#dc2626', bg:'#fee2e2', color:'#7f1d1d' },
  completed: { label:'Completed',  dot:'#2563eb', bg:'#dbeafe', color:'#1e3a5f' },
};

const fmtPrice    = (p) => p?.toLocaleString('en-IN') || '0';
const fmtDate     = (d) => new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
const fmtDateTime = (d) => new Date(d).toLocaleString('en-IN', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
const timeAgo = (date) => { const m = Math.floor((Date.now() - new Date(date)) / 60000); if (m < 1) return 'Just now'; if (m < 60) return `${m}m ago`; const h = Math.floor(m/60); if (h < 24) return `${h}h ago`; const d = Math.floor(h/24); return d < 7 ? `${d}d ago` : fmtDate(date); };

const StatusDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false); const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown',h); return () => document.removeEventListener('mousedown',h); }, []);
  const cur = STATUS_CONFIG[value] || STATUS_CONFIG.all;
  return (
    <div className="mb-dd" ref={ref}>
      <button className="mb-dd__btn" onClick={() => setOpen(!open)} type="button">
        {value !== 'all' && <span className="mb-dd__dot" style={{background:cur.dot}}/>}
        <span className="mb-dd__label">{cur.label}</span>
        <svg className={`mb-dd__chevron ${open?'open':''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && <div className="mb-dd__menu">{Object.entries(STATUS_CONFIG).map(([key,cfg]) => (
        <button key={key} type="button" className={`mb-dd__item ${value===key?'active':''}`} onClick={() => { onChange(key); setOpen(false); }}>
          <span className="mb-dd__item-dot" style={{background:cfg.dot}}/><span>{cfg.label}</span>
          {value===key && <svg className="mb-dd__tick" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
        </button>
      ))}</div>}
    </div>
  );
};

const Badge = ({ status }) => { const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending; return <span className="mb-badge" style={{background:cfg.bg,color:cfg.color}}><span className="mb-badge__dot" style={{background:cfg.dot}}/>{cfg.label}</span>; };
const Stat = ({ value, label, accent, icon }) => <div className="mb-stat" style={{'--a':accent}}><div className="mb-stat__icon">{icon}</div><div className="mb-stat__info"><span className="mb-stat__val">{value}</span><span className="mb-stat__lbl">{label}</span></div><div className="mb-stat__line"/></div>;

/* ═══════════════════════════════════════════
   AWARD TRIPSIE CASH (completed bookings)
═══════════════════════════════════════════ */
function AwardWalletSection({ booking, onAwarded, hdrs }) {
  const [amount,  setAmount]  = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState('');
  useEffect(() => { if (booking?.travelers) setAmount(String(booking.travelers * 100)); }, [booking]);

  if (booking?.walletCredited || success) return (
    <div className="mb-wallet mb-wallet--done">
      <div className="mb-wallet__done-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg></div>
      <div><p className="mb-wallet__done-title">Tripsie Cash Awarded ✓</p><p className="mb-wallet__done-sub">Cash has been credited to the customer's wallet</p></div>
    </div>
  );

  const handleAward = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setError('Enter a valid amount.'); return; }
    if (!booking?.userId) { setError('No user account linked to this booking.'); return; }
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/wallet/admin/credit`, { method:'POST', headers:hdrs(), body:JSON.stringify({ userId:booking.userId, amount:amt, adminComment:comment.trim()||`Congratulations on completing ${booking.tripTitle}! Enjoy your Tripsie Cash.`, bookingId:booking._id, bookingRef:booking.bookingId, tripTitle:booking.tripTitle }) });
      const d = await r.json();
      if (d.success) { setSuccess(true); onAwarded(booking._id, 'credited'); }
      else setError(d.message || 'Failed to award cash.');
    } catch { setError('Server error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="mb-wallet">
      <div className="mb-wallet__header">
        <div className="mb-wallet__header-icon"><svg width="16" height="16" viewBox="0 0 100 88" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="12" width="70" height="66" rx="13"/><line x1="14" y1="26" x2="55" y2="26" strokeWidth="6"/><line x1="14" y1="37" x2="45" y2="37" strokeWidth="6"/><rect x="64" y="26" width="33" height="36" rx="16"/><circle cx="80" cy="44" r="9" strokeWidth="6.5"/><circle cx="80" cy="44" r="3.5" fill="currentColor" stroke="none"/></svg></div>
        <div><p className="mb-wallet__header-title">Award Tripsie Cash</p><p className="mb-wallet__header-sub">Credit wallet for completing this trip · 1 Cash = ₹1</p></div>
      </div>
      <div className="mb-wallet__form">
        <div className="mb-wallet__row">
          <div className="mb-wallet__field">
            <label className="mb-wallet__label">Amount (₹)</label>
            <div className="mb-wallet__input-wrap"><span className="mb-wallet__prefix">₹</span><input type="number" className="mb-wallet__input" value={amount} min="1" placeholder="100" onChange={e => { setAmount(e.target.value); setError(''); }}/></div>
          </div>
          <div className="mb-wallet__quick-btns">{[50,100,200,500].map(v => <button key={v} type="button" className={`mb-wallet__quick${amount===String(v)?' active':''}`} onClick={() => setAmount(String(v))}>₹{v}</button>)}</div>
        </div>
        <div className="mb-wallet__field">
          <label className="mb-wallet__label">Message to Customer <span style={{color:'#aaa',fontWeight:400}}>(optional)</span></label>
          <textarea className="mb-wallet__textarea" rows={2} placeholder={`Congratulations on completing ${booking?.tripTitle||'the trip'}! Enjoy your Tripsie Cash.`} value={comment} onChange={e => setComment(e.target.value)}/>
          <p className="mb-wallet__hint">This message is visible to the customer in their wallet history.</p>
        </div>
        {error && <p className="mb-wallet__error"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{error}</p>}
        <button className="mb-wallet__submit" type="button" disabled={loading||!amount} onClick={handleAward}>
          {loading ? <><span className="mb-wallet__spinner"/> Crediting…</> : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>Award ₹{amount||'0'} Tripsie Cash</>}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   RELEASE WALLET REFUND (cancelled bookings)
═══════════════════════════════════════════ */
function ReleaseRefundSection({ booking, onRefundReleased, hdrs }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState('');

  if (booking?.walletRefunded || success) return (
    <div className="mb-refund mb-refund--done">
      <div className="mb-refund__done-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg></div>
      <div><p className="mb-refund__done-title">Wallet Refund Released ✓</p><p className="mb-refund__done-sub">₹{fmtPrice(booking.walletAmountUsed)} Tripsie Cash has been refunded to the customer</p></div>
    </div>
  );

  if (!booking?.walletRefundPending || !booking?.walletAmountUsed) return null;

  const handleRelease = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/wallet/admin/approve-refund/${booking._id}`, { method:'POST', headers:hdrs() });
      const d = await r.json();
      if (d.success) { setSuccess(true); onRefundReleased(booking._id); }
      else setError(d.message || 'Failed to release refund.');
    } catch { setError('Server error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="mb-refund">
      <div className="mb-refund__header">
        <div className="mb-refund__icon">
          <svg width="16" height="16" viewBox="0 0 100 88" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="12" width="70" height="66" rx="13"/><line x1="14" y1="26" x2="55" y2="26" strokeWidth="6"/><line x1="14" y1="37" x2="45" y2="37" strokeWidth="6"/><rect x="64" y="26" width="33" height="36" rx="16"/><circle cx="80" cy="44" r="9" strokeWidth="6.5"/><circle cx="80" cy="44" r="3.5" fill="currentColor" stroke="none"/></svg>
        </div>
        <div>
          <p className="mb-refund__title">Wallet Refund Pending</p>
          <p className="mb-refund__sub">Customer used ₹{fmtPrice(booking.walletAmountUsed)} Tripsie Cash for this booking</p>
        </div>
        <div className="mb-refund__amount">₹{fmtPrice(booking.walletAmountUsed)}</div>
      </div>
      <p className="mb-refund__note">The customer has been notified that their refund will be credited within 7 business days. Release it now to credit their wallet immediately.</p>
      {error && <p className="mb-wallet__error" style={{marginBottom:10}}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{error}</p>}
      <button className="mb-refund__btn" type="button" disabled={loading} onClick={handleRelease}>
        {loading ? <><span className="mb-wallet__spinner"/> Releasing…</> : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>Release ₹{fmtPrice(booking.walletAmountUsed)} Refund Now</>}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
export default function ManageBookings() {
  const [bookings,  setBookings]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('all');
  const [selected,  setSelected]  = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating,  setUpdating]  = useState(false);
  const [stats,     setStats]     = useState(null);
  const [error,     setError]     = useState('');
  // ★ trash confirmation state
  const [trashConfirm, setTrashConfirm] = useState(false);
  const [trashing,     setTrashing]     = useState(false);

  const { refreshCount } = useRecycleBin();

  const hdrs = () => ({ 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('adminToken')}` });

  const fetchBookings = useCallback(async () => {
    try { setLoading(true); setError(''); const r = await fetch(`${API_BASE_URL}/bookings/admin/all?status=${filter}`, { headers:hdrs() }); const d = await r.json(); d.success ? setBookings(d.bookings) : setError(d.message||'Failed to fetch.'); }
    catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  }, [filter]);

  const fetchStats = useCallback(async () => { try { const r = await fetch(`${API_BASE_URL}/bookings/admin/stats`, { headers:hdrs() }); const d = await r.json(); if (d.success) setStats(d.stats); } catch {} }, []);

  useEffect(() => { fetchBookings(); fetchStats(); }, [fetchBookings, fetchStats]);

  const updateStatus = async (id, status) => {
    setUpdating(true);
    try {
      const r = await fetch(`${API_BASE_URL}/bookings/admin/${id}/status`, { method:'PATCH', headers:hdrs(), body:JSON.stringify({ status }) });
      const d = await r.json();
      if (d.success) { setBookings(p => p.map(b => b._id===id ? {...b, status, isRead:true, ...(status==='cancelled' && b.walletAmountUsed > 0 ? {walletRefundPending:true} : {})} : b)); if (selected?._id===id) setSelected(p => ({...p, status, isRead:true, ...(status==='cancelled' && p.walletAmountUsed > 0 ? {walletRefundPending:true} : {})})); fetchStats(); }
      else alert(d.message||'Failed to update status.');
    } catch { alert('Failed to update. Please try again.'); }
    finally { setUpdating(false); }
  };

  const markRead = async (id) => { try { await fetch(`${API_BASE_URL}/bookings/admin/${id}/read`, { method:'PATCH', headers:hdrs() }); setBookings(p => p.map(b => b._id===id ? {...b, isRead:true} : b)); } catch {} };

  const openModal = (b) => {
    setSelected(b);
    setShowModal(true);
    setTrashConfirm(false);
    if (!b.isRead) markRead(b._id);
  };

  const closeModal = () => {
    setShowModal(false);
    setTrashConfirm(false);
  };

  // ★ Move booking to Recycle Bin (soft-delete)
  const trashBooking = async () => {
    if (!selected) return;
    setTrashing(true);
    try {
      const r = await fetch(`${API_BASE_URL}/bookings/admin/${selected._id}/trash`, { method:'PATCH', headers:hdrs() });
      const d = await r.json();
      if (d.success) {
        setBookings(p => p.filter(b => b._id !== selected._id));
        closeModal();
        fetchStats();
        refreshCount(); // update sidebar badge
      } else {
        alert(d.message || 'Failed to move to Recycle Bin.');
      }
    } catch { alert('Failed. Please try again.'); }
    finally { setTrashing(false); }
  };

  const handleWalletAwarded = (bookingId) => {
    setBookings(p => p.map(b => b._id===bookingId ? {...b, walletCredited:true} : b));
    if (selected?._id===bookingId) setSelected(p => ({...p, walletCredited:true}));
  };

  const handleRefundReleased = (bookingId) => {
    setBookings(p => p.map(b => b._id===bookingId ? {...b, walletRefunded:true, walletRefundPending:false} : b));
    if (selected?._id===bookingId) setSelected(p => ({...p, walletRefunded:true, walletRefundPending:false}));
  };

  const needsAction = (status) => ['requested','pending'].includes(status?.toLowerCase());

  return (
    <div className="mb">
      {/* Header */}
      <div className="mb__header">
        <div className="mb__header-left">
          <h1 className="mb__title">Manage Bookings</h1>
          <p className="mb__sub">View and manage customer bookings{stats?.unreadCount > 0 && <span className="mb__new-pill">{stats.unreadCount} new</span>}</p>
        </div>
        <div className="mb__header-right">
          <StatusDropdown value={filter} onChange={setFilter}/>
          <button className="mb__refresh" onClick={() => { fetchBookings(); fetchStats(); }} title="Refresh"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg></button>
        </div>
      </div>

      {/* Stats */}
      {stats && <div className="mb__stats">
        <Stat value={stats.totalBookings} label="Total Bookings" accent="#1a1e18" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}/>
        <Stat value={stats.pendingBookings} label="Pending / Requested" accent="#d97706" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}/>
        <Stat value={stats.confirmedBookings} label="Confirmed" accent="#16a34a" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}/>
        <Stat value={stats.cancelledBookings} label="Cancelled" accent="#dc2626" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}/>
        <Stat value={`₹${fmtPrice(stats.totalRevenue)}`} label="Total Revenue" accent="#2563eb" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}/>
      </div>}

      {/* Error */}
      {error && <div className="mb__error"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><span>{error}</span><button onClick={fetchBookings}>Retry</button></div>}

      {/* Table Card */}
      <div className="mb__card">
        {loading ? <div className="mb__loading"><div className="mb__spinner"/><p>Loading bookings…</p></div>
        : bookings.length === 0 ? <div className="mb__empty"><div className="mb__empty-ico"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div><h3>No bookings found</h3><p>{filter!=='all'?`No ${filter} bookings yet.`:'Bookings will appear here when customers book trips.'}</p></div>
        : (<>
          {/* Mobile cards */}
          <div className="mb__mobile-list">
            {bookings.map(b => (
              <div key={b._id} className={`mb__mobile-card ${!b.isRead?'unread':''}`} onClick={() => openModal(b)}>
                <div className="mb__mobile-card-top"><div className="mb__mobile-id">{!b.isRead&&<span className="mb__pulse-dot"/>}{b.bookingId}</div><Badge status={b.status}/></div>
                <div className="mb__mobile-name">{b.contactInfo?.name}</div>
                <div className="mb__mobile-trip">{b.tripTitle}</div>
                <div className="mb__mobile-meta">
                  <span>📅 {fmtDate(b.travelDate)}</span><span>👥 {b.travelers}</span>
                  <span className="mb__mobile-amt">₹{fmtPrice(b.grandTotal)}</span>
                  {b.walletAmountUsed > 0 && <span className="mb__mobile-wallet">₹{fmtPrice(b.walletAmountUsed)}</span>}
                  {b.walletRefundPending && <span className="mb__mobile-refund-pending">Refund Pending</span>}
                </div>
                <div className="mb__mobile-time">{timeAgo(b.createdAt)}</div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="mb__table-wrap">
            <table className="mb__table">
              <thead><tr><th>Booking ID</th><th>Customer</th><th>Trip</th><th>Travel Date</th><th>Travelers</th><th>Amount</th><th>Status</th><th>Booked</th><th></th></tr></thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b._id} className={`mb__row ${!b.isRead?'mb__row--unread':''}`} onClick={() => openModal(b)}>
                    <td><div className="mb__id-cell">{!b.isRead&&<span className="mb__pulse-dot"/>}{b.bookingId}</div></td>
                    <td><div className="mb__customer"><div className="mb__avatar">{(b.contactInfo?.name||'?').charAt(0).toUpperCase()}</div><div><div className="mb__cname">{b.contactInfo?.name||'—'}</div><div className="mb__cphone">{b.contactInfo?.phone}</div></div></div></td>
                    <td><div className="mb__trip">{b.tripTitle}</div>{b.tripLocation&&<div className="mb__trip-loc">{b.tripLocation}</div>}</td>
                    <td><span className="mb__date">{fmtDate(b.travelDate)}</span></td>
                    <td><span className="mb__travelers"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>{b.travelers}</span></td>
                    <td><div><span className="mb__amount">₹{fmtPrice(b.grandTotal)}</span>{b.walletAmountUsed>0&&<div className="mb__wallet-tag">₹{fmtPrice(b.walletAmountUsed)} Cash</div>}{b.walletRefundPending&&<div className="mb__refund-pending-tag">⏳ Refund Pending</div>}</div></td>
                    <td><Badge status={b.status}/></td>
                    <td><span className="mb__time">{timeAgo(b.createdAt)}</span></td>
                    <td><button className="mb__view-btn" onClick={e => { e.stopPropagation(); openModal(b); }}>View <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mb__footer"><span>{bookings.length} booking{bookings.length!==1?'s':''}{filter!=='all'?` · ${filter}`:''}</span></div>
        </>)}
      </div>

      {/* Modal */}
      {showModal && selected && (
        <div className="mb__overlay" onClick={closeModal}>
          <div className="mb__modal" onClick={e => e.stopPropagation()}>
            <div className="mb__modal-hd">
              <div className="mb__modal-hd-left"><Badge status={selected.status}/><div><h2 className="mb__modal-title">Booking Details</h2><span className="mb__modal-id">{selected.bookingId}</span></div></div>
              <button className="mb__modal-close" onClick={closeModal}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>

            <div className="mb__modal-body">
              {/* Action buttons */}
              {needsAction(selected.status) && (
                <div className="mb__modal-actions">
                  <button className="mb__act mb__act--confirm" disabled={updating} onClick={() => updateStatus(selected._id,'confirmed')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>{updating?'Updating…':'Confirm Booking'}</button>
                  <button className="mb__act mb__act--cancel"  disabled={updating} onClick={() => updateStatus(selected._id,'cancelled')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>{updating?'Updating…':'Cancel Booking'}</button>
                </div>
              )}
              {selected.status?.toLowerCase() === 'confirmed' && (
                <div className="mb__modal-actions">
                  <button className="mb__act mb__act--complete" disabled={updating} onClick={() => updateStatus(selected._id,'completed')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>{updating?'Updating…':'Mark as Completed'}</button>
                  <button className="mb__act mb__act--cancel"  disabled={updating} onClick={() => updateStatus(selected._id,'cancelled')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>{updating?'Updating…':'Cancel Booking'}</button>
                </div>
              )}

              {/* Award Tripsie Cash */}
              {selected.status?.toLowerCase() === 'completed' && selected.userId && (
                <AwardWalletSection booking={selected} onAwarded={handleWalletAwarded} hdrs={hdrs}/>
              )}

              {/* Release Wallet Refund */}
              {selected.status?.toLowerCase() === 'cancelled' && selected.walletAmountUsed > 0 && selected.userId && (
                <ReleaseRefundSection booking={selected} onRefundReleased={handleRefundReleased} hdrs={hdrs}/>
              )}

              {/* Customer */}
              <div className="mb__section">
                <h3 className="mb__section-title"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>Customer</h3>
                <div className="mb__grid">
                  <div className="mb__field"><span className="mb__lbl">Name</span><span className="mb__val">{selected.contactInfo?.name||'—'}</span></div>
                  <div className="mb__field"><span className="mb__lbl">Phone</span><a href={`tel:${selected.contactInfo?.countryCode||''}${selected.contactInfo?.phone}`} className="mb__val mb__val--link">{selected.contactInfo?.countryCode && selected.contactInfo.countryCode !== '+91' ? `${selected.contactInfo.countryCode} ` : ''}{selected.contactInfo?.phone}</a></div>
                  <div className="mb__field mb__field--full"><span className="mb__lbl">Email</span><a href={`mailto:${selected.contactInfo?.email}`} className="mb__val mb__val--link">{selected.contactInfo?.email}</a></div>
                  {selected.contactInfo?.city && <div className="mb__field"><span className="mb__lbl">City</span><span className="mb__val">{selected.contactInfo.city}</span></div>}
                </div>
                <a href={`https://wa.me/${selected.contactInfo?.phone?.replace(/\D/g,'')}?text=${encodeURIComponent(`Hi ${selected.contactInfo?.name}, regarding your Tripsie booking ${selected.bookingId} for "${selected.tripTitle}".`)}`} target="_blank" rel="noopener noreferrer" className="mb__wa">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Message on WhatsApp
                </a>
              </div>

              {/* Traveler Details */}
              {selected.travelerDetails?.length > 0 && (
                <div className="mb__section">
                  <h3 className="mb__section-title">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                    Traveler Details
                  </h3>
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {selected.travelerDetails.map((t, i) => (
                      <div key={i} style={{padding:'12px 14px',background:'#f9f9f9',borderRadius:8,border:'1px solid rgba(26,30,24,0.08)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                          <div style={{width:28,height:28,borderRadius:'50%',background:'#1a1e18',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.72rem',fontWeight:800,flexShrink:0}}>
                            {t.fullName ? t.fullName.charAt(0).toUpperCase() : i+1}
                          </div>
                          <span style={{fontWeight:700,fontSize:'0.88rem',color:'#1a1e18'}}>
                            {t.fullName || `Traveler ${i+1}`}
                          </span>
                          {i === 0 && <span style={{fontSize:'0.6rem',fontWeight:800,padding:'2px 7px',background:'#1a1e18',color:'#fff',borderRadius:20,textTransform:'uppercase',letterSpacing:'0.06em'}}>Lead</span>}
                        </div>
                        <div className="mb__grid" style={{gap:8}}>
                          <div className="mb__field"><span className="mb__lbl">Age</span><span className="mb__val">{t.age} yrs</span></div>
                          <div className="mb__field"><span className="mb__lbl">Gender</span><span className="mb__val">{t.gender}</span></div>
                          {t.idType && <div className="mb__field"><span className="mb__lbl">ID Type</span><span className="mb__val">{t.idType?.toUpperCase()}</span></div>}
                          {t.idNumber && <div className="mb__field"><span className="mb__lbl">ID Number</span><span className="mb__val">{t.idNumber}</span></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trip */}
              <div className="mb__section">
                <h3 className="mb__section-title"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>Trip Details</h3>
                <div className="mb__grid">
                  <div className="mb__field mb__field--full"><span className="mb__lbl">Trip Name</span><span className="mb__val">{selected.tripTitle}</span></div>
                  {selected.tripLocation && <div className="mb__field"><span className="mb__lbl">Location</span><span className="mb__val">{selected.tripLocation}</span></div>}
                  {selected.tripDuration && <div className="mb__field"><span className="mb__lbl">Duration</span><span className="mb__val">{selected.tripDuration}</span></div>}
                  <div className="mb__field"><span className="mb__lbl">Travel Date</span><span className="mb__val">{fmtDate(selected.travelDate)}</span></div>
                  <div className="mb__field"><span className="mb__lbl">Travelers</span><span className="mb__val">{selected.travelers} person{selected.travelers>1?'s':''}</span></div>
                </div>
              </div>

              {/* Pricing */}
              <div className="mb__section">
                <h3 className="mb__section-title">Pricing</h3>
                <div className="mb__pricing">
                  {(() => {
                    const origTotal = (selected.totalAmount || 0) + (selected.savings || 0);
                    const origPP    = selected.travelers > 0 ? Math.round(origTotal / selected.travelers) : origTotal;
                    return (<>
                      <div className="mb__price-row"><span>₹{fmtPrice(origPP)} × {selected.travelers}</span><span>₹{fmtPrice(origTotal)}</span></div>
                      {selected.savings > 0 && <div className="mb__price-row mb__price-row--save"><span>Discount</span><span>− ₹{fmtPrice(selected.savings)}</span></div>}
                      <div className="mb__price-row mb__price-row--gst-strike"><span>+5% GST (waived)</span><span>₹{fmtPrice(selected.gstAmount)}</span></div>
                      {selected.walletAmountUsed > 0 && <div className="mb__price-row mb__price-row--wallet"><span>Tripsie Cash</span><span>− ₹{fmtPrice(selected.walletAmountUsed)}</span></div>}
                      <div className="mb__price-row mb__price-row--total"><span>Grand Total</span><span>₹{fmtPrice(selected.grandTotal)}</span></div>
                    </>);
                  })()}
                </div>
              </div>

              {selected.specialRequests && <div className="mb__section"><h3 className="mb__section-title"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>Special Requests</h3><p className="mb__requests">{selected.specialRequests}</p></div>}

              {/* ── Meta + Recycle Bin ── */}
              <div className="mb__meta">
                <span>Booked {fmtDateTime(selected.createdAt)}</span>
                {selected.cancelledAt && <span>Cancelled {fmtDateTime(selected.cancelledAt)}</span>}
                {selected.cancelReason && <span>Reason: {selected.cancelReason}</span>}
              </div>

              {/* ★ Trash / Recycle Bin section */}
              <div className="mb__trash-zone">
                {!trashConfirm ? (
                  <button
                    className="mb__trash-btn"
                    type="button"
                    onClick={() => setTrashConfirm(true)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/>
                      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                    </svg>
                    Move to Recycle Bin
                  </button>
                ) : (
                  <div className="mb__trash-confirm">
                    <div className="mb__trash-confirm-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </div>
                    <div className="mb__trash-confirm-text">
                      <span className="mb__trash-confirm-title">Move to Recycle Bin?</span>
                      <span className="mb__trash-confirm-sub">Booking will be removed from the list. It can be restored or auto-deletes after 30 days.</span>
                    </div>
                    <div className="mb__trash-confirm-btns">
                      <button
                        className="mb__trash-cancel"
                        type="button"
                        onClick={() => setTrashConfirm(false)}
                        disabled={trashing}
                      >
                        Cancel
                      </button>
                      <button
                        className="mb__trash-confirm-btn"
                        type="button"
                        onClick={trashBooking}
                        disabled={trashing}
                      >
                        {trashing
                          ? <><span className="mb-wallet__spinner" style={{borderColor:'rgba(255,255,255,.35)',borderTopColor:'#fff'}}/>Moving…</>
                          : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>Yes, Move</>
                        }
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
